import { ArrowRight, ArrowUpRight } from 'lucide-react';
import { cn } from '@/lib/utils';

interface CtaArrowProps {
  /** Style of the wrapper. 'inverted' = pour boutons primaires terracotta (cream bg sur foreground), 'primary' = pour CTAs secondaires (terracotta bg sur cream). */
  tone?: 'inverted' | 'primary' | 'secondary';
  /** 'right' arrow = standard CTA. 'up-right' = lien externe / "voir plus". Default 'right'. */
  direction?: 'right' | 'up-right';
  className?: string;
}

/**
 * CtaArrow — Button-in-Button trailing icon (Awwwards-tier).
 * Arrow nested inside a circular wrapper at the right end of a CTA button.
 * Use within a parent button/anchor with `group` class for hover physics :
 *   - Wrapper translateX/scale on group-hover
 *   - Apple-esque haptic feel
 *
 * Usage :
 *   <button className="group ...">
 *     {label}
 *     <CtaArrow tone="inverted" />
 *   </button>
 */
export function CtaArrow({ tone = 'inverted', direction = 'right', className }: CtaArrowProps) {
  const Icon = direction === 'up-right' ? ArrowUpRight : ArrowRight;
  const toneClass = {
    inverted: 'bg-primary-foreground/15 text-primary-foreground',
    primary: 'bg-primary/10 text-primary',
    secondary: 'bg-secondary/15 text-secondary'
  }[tone];

  return (
    <span
      aria-hidden="true"
      className={cn(
        'inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-full transition-all duration-300 ease-out',
        'group-hover:scale-105 group-hover:translate-x-0.5 group-hover:-translate-y-px',
        toneClass,
        className
      )}
    >
      <Icon className="h-3.5 w-3.5" strokeWidth={1.7} />
    </span>
  );
}
